import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { stripLinkUnderline } from "@/lib/htmlTransforms";
import { FaIconPicker } from "@/components/FaIconPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  PaperPlaneTilt,
  Megaphone,
  EnvelopeSimple,
  TextB,
  TextItalic,
  Link as LinkIcon,
  Trash,
  Eye,
  PencilSimple,
  Clock,
  Prohibit,
  UsersThree,
  Newspaper,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { CampaignsTab } from "@/components/comm/CampaignsTab";
import { AudiencesTab } from "@/components/comm/AudiencesTab";
import { TemplatesTab } from "@/components/comm/TemplatesTab";
import { SubscribersTab } from "@/components/comm/SubscribersTab";

const TABS = [
  { id: "compose",     label: "Compose",     Icon: PaperPlaneTilt },
  { id: "campaigns",   label: "Campaigns",   Icon: Megaphone },
  { id: "templates",   label: "Templates",   Icon: EnvelopeSimple },
  { id: "suppression", label: "Unsubscribed",Icon: Prohibit },
  { id: "audiences",   label: "Audiences",   Icon: UsersThree },
  { id: "subscribers", label: "Subscribers", Icon: Newspaper },
];

const PKG_STATUSES = [
  { v: "", label: "Any status" },
  { v: "pending", label: "Pending" },
  { v: "in_transit", label: "In Transit" },
  { v: "ready_for_pickup", label: "Ready for Pickup" },
  { v: "delivered", label: "Delivered" },
  { v: "cancelled", label: "Cancelled" },
];

const VARIABLES = [
  { token: "{{recipient_name}}", label: "Recipient name" },
  { token: "{{customer_number}}", label: "Customer #" },
  { token: "{{company_name}}", label: "Company name" },
  { token: "{{verification_link}}", label: "Verification link (URL)" },
  { token: "{{verify_button}}", label: "Verify-email button (HTML)" },
];

export default function Communication() {
  const { user } = useAuth();
  const [tab, setTab] = useState("compose");

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="communication-page">
      <Toaster position="top-right" />
      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
          Outreach
        </div>
        <h1
          className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1"
          style={{ fontFamily: "Work Sans" }}
        >
          Communication
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Send bulk emails to your recipients and team — with templates, variable tokens, and delivery tracking.
        </p>
      </div>

      <div className="border-b border-zinc-200 mb-6 flex gap-1" role="tablist">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`comm-tab-${id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              tab === id
                ? "border-[#002BF6] text-[#002BF6]"
                : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
          >
            <Icon size={15} weight={tab === id ? "bold" : "regular"} />
            {label}
          </button>
        ))}
      </div>

      {tab === "compose"     && <ComposeTab currentUser={user} />}
      {tab === "campaigns"   && <CampaignsTab />}
      {tab === "templates"   && <TemplatesTab />}
      {tab === "suppression" && <SuppressionTab />}
      {tab === "audiences"   && <AudiencesTab />}
      {tab === "subscribers" && <SubscribersTab />}
    </div>
  );
}

// ============ COMPOSE TAB ============
function ComposeTab({ currentUser }) {
  const navigate = useNavigate();
  const [audience, setAudience] = useState("recipients");
  const [audienceLists, setAudienceLists] = useState([]);
  const [packageStatus, setPackageStatus] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [role, setRole] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("<p>Hi {{recipient_name}},</p>\n<p>We wanted to let you know…</p>");
  const [showHtml, setShowHtml] = useState(false);
  // Strip the default browser underline from every <a> in the body before
  // sending. Most courier admins prefer their links to blend into the
  // surrounding text — this toggle is opt-in and the transform runs once
  // at send/preview time (the editor display is left untouched).
  const [noLinkUnderline, setNoLinkUnderline] = useState(false);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(null);
  // Schedule-for-later state
  const [sendMode, setSendMode] = useState("now"); // "now" | "scheduled"
  const [scheduledFor, setScheduledFor] = useState(""); // datetime-local string

  const bodyRef = useRef(null);

  useEffect(() => {
    api.get("/email-templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/audience-lists").then(({ data }) => setAudienceLists(data)).catch(() => {});
  }, []);

  // Fetch subscriber count whenever "subscribers" audience is selected
  useEffect(() => {
    if (audience === "subscribers") {
      setSubscriberCount(null);
      api.get("/newsletter/subscribers?page_size=1")
        .then(({ data }) => setSubscriberCount(data.total ?? 0))
        .catch(() => setSubscriberCount(0));
    }
  }, [audience]);

  const filterPayload = useMemo(() => {
    if (audience === "recipients") {
      const f = {};
      if (packageStatus) f.package_status = packageStatus;
      if (verificationStatus) f.verification_status = verificationStatus;
      return Object.keys(f).length ? f : null;
    }
    if (audience === "team") return role ? { role } : null;
    // audience lists have no extra filter
    return null;
  }, [audience, packageStatus, verificationStatus, role]);

  const doPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/campaigns/preview", {
        audience,
        filter: filterPayload,
      });
      setPreview(data);
      setConfirmOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const doSend = async () => {
    if (!name.trim()) { toast.error("Campaign name is required"); return; }
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!body.trim()) { toast.error("Body is required"); return; }
    let scheduledIso = null;
    if (sendMode === "scheduled") {
      if (!scheduledFor) {
        toast.error("Pick a date & time for the scheduled send");
        return;
      }
      const d = new Date(scheduledFor);
      if (isNaN(d.getTime())) { toast.error("Invalid date"); return; }
      if (d.getTime() < Date.now() + 60_000) {
        toast.error("Scheduled time must be at least 1 minute in the future");
        return;
      }
      scheduledIso = d.toISOString();
    }
    setSending(true);
    try {
      const finalBody = noLinkUnderline ? stripLinkUnderline(body) : body;
      const payload = {
        name, subject, body_html: finalBody,
        audience, channel: "email",
        filter: filterPayload,
        ...(scheduledIso ? { scheduled_for: scheduledIso } : {}),
      };
      const { data } = await api.post("/campaigns", payload);
      if (data.status === "scheduled") {
        toast.success(`Scheduled to send to ${data.recipient_count} recipients on ${new Date(data.scheduled_for).toLocaleString()}`);
      } else {
        toast.success(`Campaign sent to ${data.recipient_count} recipients`);
      }
      setConfirmOpen(false);
      navigate(`/communication/campaigns/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const insertToken = (token) => {
    // Append the token to the current body. Sync effect re-applies the new
    // HTML to the editor and we re-place the caret at the very end so the
    // user can keep typing after the inserted token.
    setBody((b) => (b || "") + " " + token);
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    });
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setSubject(t.subject);
    setBody(t.body_html);
    toast.success(`Loaded "${t.name}"`);
  };

  // Sync the editable div's innerHTML with `body` ONLY when an external source
  // (template load, token insert, format command, mode toggle) sets a value
  // that differs from what the DOM already shows. Critically, we DO NOT touch
  // innerHTML while the user is typing — that would reset the caret to
  // position 0 on every keystroke. React's `dangerouslySetInnerHTML` was the
  // original culprit since it re-applies on every render.
  //
  // `showHtml` is in the deps so that switching back to Rich mode re-applies
  // the current body to the freshly-mounted contentEditable div.
  useEffect(() => {
    if (showHtml) return;
    const el = bodyRef.current;
    if (!el) return;
    if (el.innerHTML !== (body || "")) {
      el.innerHTML = body || "";
    }
  }, [body, showHtml]);

  // Mini rich-text toolbar using execCommand (works for contenteditable preview)
  const fmt = (cmd) => {
    document.execCommand(cmd, false, null);
    if (bodyRef.current) setBody(bodyRef.current.innerHTML);
  };
  const insertLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    document.execCommand("createLink", false, url);
    if (bodyRef.current) setBody(bodyRef.current.innerHTML);
  };
  // Insert an inline SVG icon at the current cursor position. Falls back to
  // appending at the end of the body when the editor isn't focused.
  const insertIconSvg = (svgHtml) => {
    const el = bodyRef.current;
    if (!el) return;
    el.focus();
    const inserted = document.execCommand("insertHTML", false, svgHtml + " ");
    if (!inserted) {
      // Older browsers / strict policies — fall back to appending
      el.innerHTML = (el.innerHTML || "") + " " + svgHtml + " ";
    }
    setBody(el.innerHTML);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      {/* Editor column */}
      <div className="bg-white border border-zinc-200 rounded-md p-5 space-y-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
            Campaign name
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. November update"
            className="mt-1.5"
            data-testid="campaign-name"
          />
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
            Subject
          </Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject — supports {{recipient_name}}"
            className="mt-1.5"
            data-testid="campaign-subject"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
              Body
            </Label>
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setShowHtml(false)}
                className={`px-2 py-1 rounded ${!showHtml ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}
                data-testid="body-mode-rich"
              >
                <PencilSimple size={11} className="inline mr-1" /> Rich
              </button>
              <button
                type="button"
                onClick={() => setShowHtml(true)}
                className={`px-2 py-1 rounded ${showHtml ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}
                data-testid="body-mode-html"
              >
                HTML
              </button>
            </div>
          </div>
          {!showHtml ? (
            <>
              <div className="flex gap-1 mb-2 border border-zinc-200 rounded p-1 bg-zinc-50">
                <ToolbarBtn onClick={() => fmt("bold")} title="Bold"><TextB size={14} weight="bold" /></ToolbarBtn>
                <ToolbarBtn onClick={() => fmt("italic")} title="Italic"><TextItalic size={14} /></ToolbarBtn>
                <ToolbarBtn onClick={insertLink} title="Insert link"><LinkIcon size={14} /></ToolbarBtn>
                <div className="w-px h-5 bg-zinc-300 mx-1 self-center" />
                <FaIconPicker
                  mode="insert"
                  triggerLabel="Icon"
                  triggerVariant="ghost"
                  onPick={insertIconSvg}
                  testId="compose-icon-picker"
                />
                <div className="w-px h-5 bg-zinc-300 mx-1 self-center" />
                {VARIABLES.map((v) => (
                  <button
                    key={v.token}
                    onClick={() => insertToken(v.token)}
                    type="button"
                    className="text-[11px] px-2 py-1 rounded bg-white border border-zinc-200 hover:bg-zinc-100 text-zinc-700"
                    data-testid={`insert-${v.token.replace(/[{}]/g,"")}`}
                  >
                    {v.token}
                  </button>
                ))}
              </div>
              <div
                ref={bodyRef}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => setBody(e.currentTarget.innerHTML)}
                className="min-h-[260px] border border-zinc-300 rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002BF6]/30"
                data-testid="body-rich-editor"
              />
            </>
          ) : (
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="font-mono text-xs"
              data-testid="body-html-editor"
            />
          )}
          <div className="mt-1.5 text-[11px] text-zinc-500">
            Tip: insert variable tokens like <code className="bg-zinc-100 px-1 rounded">{"{{recipient_name}}"}</code> — they'll be substituted per recipient before sending.
          </div>
        </div>
      </div>

      {/* Sidebar column */}
      <div className="space-y-4">
        <div className="bg-white border border-zinc-200 rounded-md p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-3">
            Audience
          </div>
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger data-testid="audience-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recipients">Recipients (customers)</SelectItem>
              <SelectItem value="team">Team users</SelectItem>
              <SelectItem value="subscribers">Newsletter subscribers</SelectItem>
              {audienceLists.length > 0 && audienceLists.map((al) => (
                <SelectItem key={al.id} value={`list:${al.id}`}>
                  {al.name} ({al.member_count} {al.member_count === 1 ? "member" : "members"})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {audience === "subscribers" && (
            <div className="mt-3 flex items-center gap-2" data-testid="subscriber-count-badge">
              {subscriberCount === null ? (
                <span className="text-xs text-zinc-400">Loading count…</span>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 13c0-2.761 2.239-5 5-5s5 2.239 5 5H3z"/></svg>
                    {subscriberCount.toLocaleString()} active subscriber{subscriberCount !== 1 ? "s" : ""}
                  </span>
                  {subscriberCount === 0 && (
                    <span className="text-[10px] text-zinc-400">No active subscribers yet</span>
                  )}
                </>
              )}
            </div>
          )}

          {audience === "recipients" && (
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                Filter by package status
              </div>
              <Select value={packageStatus || "all"} onValueChange={(v) => setPackageStatus(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-pkg-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  {PKG_STATUSES.filter(s => s.v).map(s => (
                    <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                  Filter by email verification
                </div>
                <Select
                  value={verificationStatus || "all"}
                  onValueChange={(v) => setVerificationStatus(v === "all" ? "" : v)}
                >
                  <SelectTrigger data-testid="filter-verification-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any (verified or not)</SelectItem>
                    <SelectItem value="unverified">Only unverified emails</SelectItem>
                    <SelectItem value="verified">Only verified emails</SelectItem>
                  </SelectContent>
                </Select>
                {verificationStatus === "unverified" && (
                  <div className="mt-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Insert <code className="font-mono">{"{{verify_button}}"}</code> in your email body — it auto-renders a personalised verify CTA only for unverified recipients.
                  </div>
                )}
              </div>
            </div>
          )}

          {audience === "team" && (
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                Filter by role
              </div>
              <Select value={role || "all"} onValueChange={(v) => setRole(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any role</SelectItem>
                  <SelectItem value="company_admin">Company Admin</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher</SelectItem>
                  <SelectItem value="user">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Styling toggle — admins who want their links to blend into body
              text (no default browser underline) opt-in here. Applied to
              every <a> in body_html on send. */}
          <label className="mt-4 flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={noLinkUnderline}
              onChange={(e) => setNoLinkUnderline(e.target.checked)}
              data-testid="toggle-no-link-underline"
              className="mt-0.5 accent-[#002BF6]"
            />
            <div className="flex-1">
              <div className="text-xs font-semibold text-zinc-800">
                Remove underlines from links
              </div>
              <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">
                Strips the default <code className="font-mono">text-decoration</code> on every
                URL in the body — keeps your email cleaner on dark backgrounds.
              </div>
            </div>
          </label>
        </div>

        {templates.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-3">
              Load template
            </div>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger data-testid="template-select"><SelectValue placeholder="Pick a template…" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button
          className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white"
          onClick={doPreview}
          disabled={previewLoading}
          data-testid="preview-send-btn"
        >
          <Eye size={15} className="mr-1.5" /> {previewLoading ? "Loading…" : "Preview & Send"}
        </Button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg" data-testid="confirm-send-dialog">
          <DialogHeader>
            <DialogTitle>Confirm send</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-zinc-100 pb-2">
                <span className="text-zinc-500">Recipients</span>
                <span className="font-semibold" data-testid="confirm-count">{preview.count}</span>
              </div>
              <div className="flex justify-between border-b border-zinc-100 pb-2">
                <span className="text-zinc-500">Plan ({preview.plan_id})</span>
                <span>
                  {preview.plan_email_limit_month === -1
                    ? "Unlimited"
                    : `${preview.plan_email_used_month}/${preview.plan_email_limit_month} used this month`}
                </span>
              </div>
              {preview.would_exceed_limit && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-xs" data-testid="quota-exceed-warning">
                  This send would exceed your plan's monthly email quota.
                  Reduce the audience or upgrade your plan.
                </div>
              )}
              {preview.count === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-700 text-xs">
                  No recipients match. Adjust your filter.
                </div>
              )}
              {preview.sample?.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                    Sample (first {preview.sample.length})
                  </div>
                  <div className="max-h-32 overflow-auto border border-zinc-200 rounded text-xs">
                    {preview.sample.map((s, i) => (
                      <div key={i} className="px-2 py-1 border-b border-zinc-100 last:border-0 flex justify-between">
                        <span className="truncate">{s.name || "—"}</span>
                        <span className="text-zinc-500 truncate ml-2">{s.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send mode: now or schedule for later */}
              <div className="pt-2 border-t border-zinc-100 space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">When to send</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSendMode("now")}
                    className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors ${sendMode === "now" ? "bg-[#002BF6] text-white border-[#002BF6]" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
                    data-testid="send-mode-now"
                  >
                    <PaperPlaneTilt size={13} weight="bold" className="inline mr-1.5 -mt-0.5" />
                    Send now
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendMode("scheduled")}
                    className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors ${sendMode === "scheduled" ? "bg-[#002BF6] text-white border-[#002BF6]" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
                    data-testid="send-mode-scheduled"
                  >
                    <Clock size={13} weight="bold" className="inline mr-1.5 -mt-0.5" />
                    Schedule
                  </button>
                </div>
                {sendMode === "scheduled" && (
                  <div>
                    <Input
                      type="datetime-local"
                      value={scheduledFor}
                      onChange={(e) => setScheduledFor(e.target.value)}
                      className="text-sm"
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      data-testid="scheduled-for-input"
                    />
                    <div className="text-[11px] text-zinc-500 mt-1.5">
                      Campaign will dispatch automatically at this time (your local timezone).
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</Button>
            <Button
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              onClick={doSend}
              disabled={sending || !preview || preview.count === 0 || preview.would_exceed_limit || (sendMode === "scheduled" && !scheduledFor)}
              data-testid="confirm-send-btn"
            >
              {sendMode === "scheduled" ? <Clock size={15} className="mr-1.5" /> : <PaperPlaneTilt size={15} className="mr-1.5" />}
              {sending
                ? (sendMode === "scheduled" ? "Scheduling…" : "Sending…")
                : (sendMode === "scheduled"
                    ? `Schedule for ${preview?.count || 0}`
                    : `Send to ${preview?.count || 0}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="px-2 py-1 rounded hover:bg-white text-zinc-700 hover:text-zinc-900"
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Suppression / Unsubscribed list                                    */
/*  Shows every email that has opted out of this company's campaigns.  */
/*  Company admins can also un-suppress or manually add addresses.     */
/* ------------------------------------------------------------------ */
function SuppressionTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-suppression");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load unsubscribed list");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSaving(true);
    try {
      await api.post("/email-suppression", { email });
      toast.success("Added to suppression list");
      setNewEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.email} from suppression? They'll receive campaigns again.`)) return;
    try {
      await api.delete(`/email-suppression/${row.id}`);
      toast.success("Removed");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const filtered = rows.filter((r) =>
    !q || r.email?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="suppression-tab">
      <div className="mb-4">
        <div className="text-sm text-zinc-600 mb-3">
          Recipients on this list will be skipped by every future campaign — RFC 8058 one-click
          unsubscribes land here automatically. You can also manually add or remove addresses.
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="Search by email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-sm"
            data-testid="suppression-search"
          />
          <div className="flex-1" />
          <Input
            placeholder="Add email to suppression"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="max-w-xs"
            data-testid="suppression-add-input"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
          <Button onClick={add} disabled={saving} data-testid="suppression-add-btn">
            {saving ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="suppression-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Source</th>
              <th className="px-5 py-3 font-semibold">Unsubscribed at</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-zinc-500">
                  {rows.length === 0
                    ? "No one has unsubscribed yet."
                    : "No matches for your search."}
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 last:border-0" data-testid={`suppression-row-${r.id}`}>
                <td className="px-5 py-3 font-medium text-zinc-900">{r.email}</td>
                <td className="px-5 py-3 text-zinc-600 capitalize text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-zinc-100 text-zinc-700">
                    {r.source || "unknown"}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-zinc-600 whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-5 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(r)}
                    data-testid={`suppression-remove-${r.id}`}
                    className="text-xs"
                    title="Remove from suppression (re-subscribe)"
                  >
                    <Trash size={12} className="mr-1" /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length > 0 && (
          <div className="px-5 py-2 text-xs text-zinc-500 border-t border-zinc-100">
            Showing {filtered.length} of {rows.length} unsubscribed email{rows.length === 1 ? "" : "s"}.
          </div>
        )}
      </div>
    </div>
  );
}
